import React, { useState, useRef } from 'react';
import {
  Box, Button, Typography, Alert, CircularProgress,
  Collapse, Paper, Checkbox, FormControlLabel,
  RadioGroup, Radio, MenuItem, TextField,
} from '@mui/material';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import EditIcon from '@mui/icons-material/Edit';
import api from '../services/api.js';
import { useCkInside } from '@NP-Webber/crm-kit/dist/next/index.js';
import ImportSectionNext from './ImportSectionNext.jsx';

/**
 * ImportSection — רכיב ייבוא Excel מתקפל לשימוש חוזר.
 *
 * Props:
 *  label            — כותרת הסעיף (ל"ייבוא <label>")
 *  endpoint         — נתיב POST לשרת (ל-api)
 *  templateEndpoint — נתיב GET להורדת תבנית
 *  description      — תיאור קצר / הנחיות
 *  onImportDone     — callback אחרי ייבוא מוצלח
 *  extraActions     — JSX נוסף שיוצג בתחתית (אופציונלי)
 *  entityType       — סוג הישות (members/kolels/exams/grades) לעדכון חלקי
 *  identifierColumns — [{ field, label }] עמודות מזהות אפשריות לעדכון חלקי
 *  extraFormData      — שדות נוספים ל-FormData (למשל periodId)
 *  periodName         — שם המועד להצגה בהנחיית ייבוא
 */
/** In the new design (under the crm-kit/next shell) the same props render ImportSectionNext. */
const ImportSection = (props) => {
  const inside = useCkInside();
  return inside ? <ImportSectionNext {...props} /> : <ImportSectionLegacy {...props} />;
};

const ImportSectionLegacy = ({
  label,
  endpoint,
  templateEndpoint,
  description,
  onImportDone,
  extraActions,
  entityType,
  identifierColumns,
  extraFormData,
  periodName,
  uploadDisabled,
  uploadDisabledReason,
}) => {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [replace, setReplace] = useState(false);
  const [partialMode, setPartialMode] = useState(false);
  const [identifierColumn, setIdentifierColumn] = useState('');
  const [fileColumn, setFileColumn] = useState('');
  const [fileHeaders, setFileHeaders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const f = e.target.files[0] || null;
    setFile(f);
    setResult(null);
    setError('');
    setFileColumn('');
    if (f && partialMode) fetchHeaders(f);
  };

  const fetchHeaders = async (f) => {
    try {
      const formData = new FormData();
      formData.append('file', f);
      const { data } = await api.post('/import/preview-headers', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setFileHeaders(data.headers || []);
    } catch {
      setFileHeaders([]);
    }
  };

  const handleUpload = async () => {
    if (!file) return;
    if (partialMode && !identifierColumn) {
      setError('יש לבחור שדה מזהה במערכת');
      return;
    }
    if (partialMode && !fileColumn) {
      setError('יש לבחור עמודה מהקובץ שמתאימה לשדה המזהה');
      return;
    }
    setLoading(true);
    setResult(null);
    setError('');
    try {
      const formData = new FormData();
      formData.append('file', file);

      let url;
      if (partialMode && entityType) {
        formData.append('identifierColumn', identifierColumn);
        formData.append('fileColumn', fileColumn);
        url = `/import/partial-update/${entityType}`;
      } else {
        formData.append('replace', replace ? 'true' : 'false');
        url = endpoint;
      }

      // append extra fields (e.g. period, registeredAt)
      if (extraFormData) {
        Object.entries(extraFormData).forEach(([k, v]) => {
          if (v != null && v !== '') formData.append(k, v);
        });
      }

      const { data } = await api.post(url, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(data);
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      if (onImportDone) onImportDone(data);
    } catch (err) {
      setError(err.response?.data?.error || 'שגיאה בייבוא הקובץ');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await api.get(templateEndpoint, { responseType: 'blob' });
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `template_${label}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      // ignore
    }
  };

  return (
    <Paper variant="outlined" sx={{ mt: 3, borderRadius: 2 }}>
      <Box
        sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          px: 2, py: 1.5, cursor: 'pointer', userSelect: 'none',
        }}
        onClick={() => setOpen((o) => !o)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <UploadFileOutlinedIcon fontSize="small" color="action" />
          <Typography variant="subtitle2" fontWeight={600}>
            ייבוא {label}
          </Typography>
        </Box>
        {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
      </Box>

      <Collapse in={open}>
        <Box sx={{ px: 2, pb: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          {description && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5, mb: 1 }}>
              {description}
            </Typography>
          )}

          {extraFormData?.periodId && periodName && (
            <Typography variant="caption" color="primary.main" sx={{ display: 'block', mb: 1, fontWeight: 600 }}>
              הנתונים ישויכו למועד: {periodName}
            </Typography>
          )}

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mt: 1 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={handleDownloadTemplate}
            >
              הורד תבנית
            </Button>

            <Button
              variant="outlined"
              size="small"
              component="label"
            >
              בחר קובץ Excel
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                hidden
                onChange={handleFileChange}
              />
            </Button>

            {file && (
              <Typography variant="caption" color="text.secondary">
                {file.name}
              </Typography>
            )}
          </Box>

          <FormControlLabel
            control={<Checkbox checked={replace} onChange={(e) => { setReplace(e.target.checked); if (e.target.checked) setPartialMode(false); }} size="small" />}
            label={<Typography variant="caption">החלף נתונים קיימים (מחיקה ויצירה מחדש)</Typography>}
            sx={{ mt: 1, display: 'block' }}
          />

          {identifierColumns?.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={partialMode}
                    onChange={(e) => {
                      setPartialMode(e.target.checked);
                      if (e.target.checked) setReplace(false);
                      if (!e.target.checked) {
                        setIdentifierColumn('');
                        setFileColumn('');
                        setFileHeaders([]);
                      } else if (file) {
                        fetchHeaders(file);
                      }
                    }}
                    size="small"
                    color="info"
                  />
                }
                label={
                  <Typography variant="caption" color={partialMode ? 'info.main' : 'text.secondary'}>
                    <EditIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
                    עדכון חלקי (עדכון עמודות בלבד ללא מחיקה/הוספה)
                  </Typography>
                }
                sx={{ display: 'block' }}
              />
              {partialMode && (
                <Box sx={{ mt: 1, p: 1.5, bgcolor: 'info.50', border: '1px solid', borderColor: 'info.light', borderRadius: 1 }}>
                  <Typography variant="caption" display="block" mb={0.5}>
                    בחר איזו עמודה מהקובץ מתאימה לאיזה שדה במערכת:
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                    <TextField
                      select
                      size="small"
                      value={fileColumn}
                      onChange={(e) => setFileColumn(e.target.value)}
                      sx={{ minWidth: 160 }}
                      label="עמודה בקובץ"
                      disabled={!file || fileHeaders.length === 0}
                    >
                      {fileHeaders.map((h) => (
                        <MenuItem key={h} value={h}>{h}</MenuItem>
                      ))}
                    </TextField>

                    <Typography variant="caption" sx={{ pb: 1 }}>⇐</Typography>

                    <TextField
                      select
                      size="small"
                      value={identifierColumn}
                      onChange={(e) => setIdentifierColumn(e.target.value)}
                      sx={{ minWidth: 160 }}
                      label="שדה במערכת"
                    >
                      {identifierColumns.map((col) => (
                        <MenuItem key={col.field} value={col.field}>{col.label}</MenuItem>
                      ))}
                    </TextField>
                  </Box>
                </Box>
              )}
            </Box>
          )}

          <Box sx={{ mt: 1.5, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="small"
              color={partialMode ? 'info' : 'primary'}
              disabled={!file || loading || (partialMode && (!identifierColumn || !fileColumn)) || !!uploadDisabled}
              onClick={handleUpload}
              startIcon={loading ? <CircularProgress size={14} /> : partialMode ? <EditIcon /> : undefined}
            >
              {loading ? 'מעבד...' : partialMode ? 'עדכון חלקי' : 'ייבוא'}
            </Button>
            {uploadDisabled && uploadDisabledReason && (
              <Typography variant="caption" color="error">{uploadDisabledReason}</Typography>
            )}
          </Box>

          {result && (
            <Alert severity="success" sx={{ mt: 1.5 }} onClose={() => setResult(null)}>
              {result.message}
              {result.errors?.length > 0 && (
                <Box component="ul" sx={{ mt: 0.5, mb: 0, pl: 2, fontSize: 12 }}>
                  {result.errors.slice(0, 5).map((e, i) => (
                    <li key={i}>{typeof e === 'string' ? e : `שורה ${e.row}: ${e.error}`}</li>
                  ))}
                  {result.errors.length > 5 && <li>ועוד {result.errors.length - 5} שגיאות...</li>}
                </Box>
              )}
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mt: 1.5 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {extraActions && (
            <Box sx={{ mt: 2 }}>{extraActions}</Box>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
};

export default ImportSection;
